import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Calendar,
  UtensilsCrossed,
  Users,
  Image,
  FileText,
  ExternalLink,
  Search,
  Info,
  BarChart3,
} from 'lucide-react';
import { getScoreColor } from '@/lib/utils';
import type { EventsCateringData } from '@/types/audit';

interface EventsCateringPanelProps {
  data: EventsCateringData;
}

export function EventsCateringPanel({ data }: EventsCateringPanelProps) {
  return (
    <div className="space-y-8">
      {/* Score Overview */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Events & Catering Analysis</h3>
          <p className="text-sm text-muted-foreground">
            High-margin business opportunities
          </p>
        </div>
        <div className="text-right">
          <p className={`text-3xl font-light ${getScoreColor(data.score)}`}>
            {data.score}
          </p>
          <p className="text-xs text-muted-foreground">optimization score</p>
        </div>
      </div>

      {/* Issues Banner */}
      {data.issues.filter(i => i.severity === 'error').length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-400">
                {data.issues.filter(i => i.severity === 'error').length} critical
                issue{data.issues.filter(i => i.severity === 'error').length > 1 ? 's' : ''} found
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                {data.issues
                  .filter(i => i.severity === 'error')
                  .map((issue, i) => (
                    <li key={i}>{issue.message}</li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Events Page Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Private Events Page
          </h4>
        </div>

        {data.eventsPage.exists ? (
          <div className="space-y-4">
            <div className="bg-secondary/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500/70" />
                  <span className="font-medium">Events Page Found</span>
                </div>
                {data.eventsPage.url && (
                  <a
                    href={data.eventsPage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    View Page <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>

            <ContextNote text="A well-optimized events page is one of the highest-margin opportunities — private dining and event bookings often generate 3-5x the revenue of regular covers." />

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <FeatureCheck label="Photo Gallery" checked={data.eventsPage.hasGallery} icon={<Image className="h-4 w-4" />} />
              <FeatureCheck label="Capacity Info" checked={data.eventsPage.hasCapacityInfo} icon={<Users className="h-4 w-4" />} />
              <FeatureCheck label="Floor Plans" checked={data.eventsPage.hasFloorPlans} icon={<FileText className="h-4 w-4" />} />
              <FeatureCheck label="Virtual Tour" checked={data.eventsPage.hasVirtualTour} />
              <FeatureCheck label="Testimonials" checked={data.eventsPage.hasTestimonials} />
            </div>

            {/* Private Events Details */}
            {data.privateEventsInfo && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <h5 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Private Events Details
                  </h5>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FeatureCheck label="Room Descriptions" checked={data.privateEventsInfo.hasRoomDescriptions} />
                  <FeatureCheck label="Private Dining" checked={data.privateEventsInfo.mentionsPrivateDining} />
                  <FeatureCheck label="Full Buyout Option" checked={data.privateEventsInfo.mentionsBuyout} />
                  <FeatureCheck label="Capacity Details" checked={data.privateEventsInfo.hasCapacityInfo} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-orange-400" />
              <div>
                <span className="font-medium text-orange-400">No Events Page Found</span>
                <p className="text-sm text-muted-foreground mt-1">
                  Private events are typically high-margin. Consider adding a dedicated page.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Inquiry Form Section */}
      {data.eventsPage.exists && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Event Inquiry Form
            </h4>
          </div>

          {data.inquiryForm.exists ? (
            <div className="space-y-3">
              <div className="bg-secondary/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">Form Analysis</span>
                  <span className="text-xs text-muted-foreground">
                    {data.inquiryForm.fieldCount} fields
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <FeatureCheck label="Date Field" checked={data.inquiryForm.hasDateField} small />
                  <FeatureCheck label="Guest Count" checked={data.inquiryForm.hasGuestCountField} small />
                  <FeatureCheck label="Event Type" checked={data.inquiryForm.hasEventTypeField} small />
                  <FeatureCheck label="Budget Field" checked={data.inquiryForm.hasBudgetField} small />
                </div>
              </div>

              <ContextNote text="Every missing form field is a follow-up phone call. The more information you capture upfront, the faster your events team can close the booking." />

              {!data.inquiryForm.hasTracking && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm">
                    <XCircle className="h-4 w-4 text-red-400" />
                    <span className="text-red-400">
                      Form submissions not tracked — losing visibility into leads
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-400" />
                <span className="text-red-400">No inquiry form found on events page</span>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Event Tracking Status */}
      {data.tracking && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-muted-foreground" />
            <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Event Tracking
            </h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <FeatureCheck label="Inquiry Tracking" checked={data.tracking.hasInquiryTracking} />
            <FeatureCheck label="Form Submit Tracking" checked={data.tracking.hasFormSubmitTracking} />
            <FeatureCheck label="Phone Click Tracking" checked={data.tracking.hasPhoneClickTracking} />
          </div>
        </section>
      )}

      {/* Catering Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
          <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Catering Page
          </h4>
        </div>

        {data.cateringPage.exists ? (
          <div className="space-y-4">
            <div className="bg-secondary/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500/70" />
                  <span className="font-medium">Catering Page Found</span>
                </div>
                {data.cateringPage.url && (
                  <a
                    href={data.cateringPage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    View Page <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>

            <ContextNote text="Catering orders are repeat business with high average order values. Making it easy to browse menus and place orders directly drives revenue." />

            <div className="grid grid-cols-2 gap-3">
              <FeatureCheck label="Catering Menu" checked={data.cateringPage.hasMenu} />
              <FeatureCheck label="Pricing Info" checked={data.cateringPage.hasPricing} />
              <FeatureCheck label="Minimum Order" checked={data.cateringPage.hasMinimumOrder} />
              <FeatureCheck label="Delivery Info" checked={data.cateringPage.hasDeliveryInfo} />
            </div>

            {data.cateringInquiry.hasOnlineOrdering && (
              <div className="bg-secondary/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-500/70" />
                  <span>
                    Online ordering available
                    {data.cateringInquiry.orderingPlatform && (
                      <span className="text-muted-foreground ml-1">
                        via {data.cateringInquiry.orderingPlatform}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-secondary/30 border border-border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-muted-foreground" />
              <div>
                <span className="font-medium">No Catering Page Found</span>
                <p className="text-sm text-muted-foreground mt-1">
                  Consider if catering could be a revenue opportunity for this location.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* SEO Signals */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Search className="h-5 w-5 text-muted-foreground" />
          <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            SEO Signals
          </h4>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FeatureCheck label="Event Keywords" checked={data.seoSignals.hasEventKeywords} />
          <FeatureCheck label="Catering Keywords" checked={data.seoSignals.hasCateringKeywords} />
          <FeatureCheck label="Event Schema" checked={data.seoSignals.hasEventSchema} />
          <FeatureCheck label="LocalBusiness Schema" checked={data.seoSignals.hasLocalBusinessSchema} />
        </div>
      </section>

      {/* All Issues */}
      {data.issues.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-muted-foreground" />
            <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              All Issues ({data.issues.length})
            </h4>
          </div>
          <ul className="space-y-2">
            {data.issues.map((issue, i) => (
              <li
                key={i}
                className={`text-sm p-3 rounded-lg flex items-start gap-2 ${
                  issue.severity === 'error'
                    ? 'bg-red-500/10 border border-red-500/20'
                    : issue.severity === 'warning'
                    ? 'bg-orange-500/10 border border-orange-500/20'
                    : 'bg-secondary/30 border border-border'
                }`}
              >
                {issue.severity === 'error' ? (
                  <XCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                ) : issue.severity === 'warning' ? (
                  <AlertTriangle className="h-4 w-4 text-orange-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <Info className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                )}
                <span>{issue.message}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ContextNote({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5 px-4 py-3 bg-secondary/20 rounded-lg border border-border/40">
      <Info className="h-4 w-4 text-muted-foreground/60 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function FeatureCheck({
  label,
  checked,
  icon,
  small = false,
}: {
  label: string;
  checked: boolean;
  icon?: React.ReactNode;
  small?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 ${
        small ? '' : 'bg-secondary/20 rounded-lg p-3'
      }`}
    >
      {checked ? (
        <CheckCircle2 className={`${small ? 'h-3 w-3' : 'h-4 w-4'} text-green-500/70`} />
      ) : (
        <XCircle className={`${small ? 'h-3 w-3' : 'h-4 w-4'} text-red-400/50`} />
      )}
      {icon && <span className="text-muted-foreground">{icon}</span>}
      <span className={`${small ? 'text-xs' : 'text-sm'} ${checked ? '' : 'text-muted-foreground'}`}>
        {label}
      </span>
    </div>
  );
}
